import React, { useCallback, useState } from "react";
import {
  Box,
  Dialog,
  Button,
  Stack,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  LinkOff as LinkOffIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";

import FieldOverlay from "./FieldOverlay";
import SelectionHandles from "./SelectionHandles";
import { pdfRectToCanvasRect } from "../../../../../../../utils/coordinateUtils";

const cleanPdfTextForLabel = (text = "") =>
  String(text || "")
    .replace(/[_\-.:*]{3,}/g, " ")
    .replace(/^[_\-.:*\s]+/, "")
    .replace(/[_\-.:*\s]+$/, "")
    .replace(/\s+/g, " ")
    .trim();

const SMALL_TITLE_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "in",
  "nor",
  "of",
  "on",
  "or",
  "the",
  "to",
  "up",
  "via",
  "with",
]);

const capitalizeWord = (word = "") =>
  String(word || "")
    .toLowerCase()
    .replace(/^([a-z])/, (match) => match.toUpperCase())
    .replace(/(['’])S\b/g, "$1s");

const toTitleCase = (text = "") =>
  String(text || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index, words) => {
      const normalizedWord = word.replace(/^[^a-z]+|[^a-z]+$/gi, "");
      const isSmallWord = SMALL_TITLE_WORDS.has(normalizedWord);
      const isEdgeWord = index === 0 || index === words.length - 1;

      return isSmallWord && !isEdgeWord ? word : capitalizeWord(word);
    })
    .join(" ");

const baseViewPort = {
  width: 612,
  height: 792,
  rotation: 0,
  transform: [1, 0, 0, -1, 0, 792],
};

const FormFieldOverlayLayer = ({
  currentProcessingFile,
  pageNumber,
  scale,
  selectToolActive,
  typingToolActive,
  highlightFields,
  textOverlayDisplayMode,
  focusedField,
  setFocusedField,
  currCursor,
  unassignedFieldNames,
  fixedInputs,
  inputRef,
  childInputRef,
  pdfContainerRef,
  setCurrentProcessingFile,
  setStartPoint,
  setRect,
  setIsDrawing,
  setCurrCursor,
  setFreeSelectStartPoint,
  setMoving,
  setIsSelecting,
  setInitialMousePosition,
  setInitialInputSize,
  setInputPosition,
  setSelectionRect,
  setResizing,
  setResizeDirection,
  handleMouseDown,
  handleFieldChange,
  undoRedoStats,
  activeEditorTool,
  isPreview = false,
}) => {
  const pageHeight =
    currentProcessingFile?.document?.tempFields?.currentDocument
      ?.actualHeight || 792;
  const viewport =
    currentProcessingFile?.document?.tempFields?.currentDocument?.viewport ||
    null;
  const pageXMin =
    currentProcessingFile?.document?.tempFields?.currentDocument?.pageXMin || 0;
  const pageYMin =
    currentProcessingFile?.document?.tempFields?.currentDocument?.pageYMin || 0;
  const scaleFactor =
    currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor ||
    1;

  const [openEditBlockDialog, setOpenEditBlockDialog] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [deletedFields, setDeletedFields] = useState([]);

  const buildLabelFromSelectedPdfText = React.useCallback((tempFields) => {
    const primaryText = cleanPdfTextForLabel(
      tempFields?.selectedPdfTextItem?.text || "",
    );
    const extraTexts = (tempFields?.selectedExtraPdfTextItems || [])
      .map((item) => cleanPdfTextForLabel(item?.text || ""))
      .filter(Boolean);

    return toTitleCase(
      [primaryText, ...extraTexts]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim(),
    );
  }, []);

  const assignLabelFromSelectedPdfText = React.useCallback(
    (prevFile, field) => {
      const tempFields = prevFile?.document?.tempFields || {};
      const nextLabel = buildLabelFromSelectedPdfText(tempFields);

      if (!nextLabel) {
        return prevFile;
      }

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...tempFields,
            formFields: (tempFields.formFields || []).map((existingField) =>
              existingField?.name === field?.name
                ? {
                    ...existingField,
                    field_label: nextLabel,
                    label: nextLabel,
                  }
                : existingField,
            ),
            selectedFormField: null,
            selectedExtraFields: [],
            selectedPdfTextItem: null,
            selectedExtraPdfTextItems: [],
          },
        },
      };
    },
    [buildLabelFromSelectedPdfText],
  );

  const handleCloseEditBlockDialog = () => {
    setOpenEditBlockDialog(false);
    setSelectedBlock(null);
  };

  const pickRectComponent = (rect = {}, upper, lower, fallback = 0) => {
    const value = rect?.[upper] ?? rect?.[lower] ?? fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number(fallback) || 0;
  };

  const getBoundingBox = useCallback(
    (fields, padding = 8) => {
      const currentDocument =
        currentProcessingFile?.document?.tempFields?.currentDocument || {};

      const minY = Math.min(...fields.map((f) => f.y));
      const viewport = currentDocument?.viewport || null;

      const topField = fields?.length
        ? fields.reduce((maxItem, item) =>
            item.y < maxItem.y ? item : maxItem,
          )
        : {};

      const y = pickRectComponent(topField, "Y", "y", 0);

      const maxY = Math.max(...fields.map((f) => f.y + f.height));
      return {
        x: 20,
        y: y - 3,
        width: viewport?.width - 50,
        height: (maxY - minY + padding * 0.5) * scaleFactor,
      };
    },
    [currentProcessingFile?.document?.tempFields?.currentDocument, scaleFactor],
  );

  const handleEditConfirm = (isAll) => {
    const tempFields = currentProcessingFile?.document?.tempFields;

    const allSelectedField =
      isAll ||
      tempFields?.formFields?.filter(
        (f) => f?.block_id === selectedBlock && !f?.name?.startsWith("block__"),
      )?.length === deletedFields?.length;

    const updatedFormFields = tempFields?.formFields?.map((f) => {
      if (
        allSelectedField &&
        f?.block_id === selectedBlock &&
        !f?.name?.startsWith("block__")
      ) {
        return {
          ...f,
          block_id: "",
          block_label: "",
        };
      } else if (deletedFields?.includes(f?.name)) {
        return {
          ...f,
          block_id: "",
          block_label: "",
        };
      } else {
        return f;
      }
    });

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: allSelectedField
            ? updatedFormFields?.filter((f) => f?.block_id !== selectedBlock)
            : updatedFormFields?.map((f) => {
                if (
                  f?.block_id === selectedBlock &&
                  f?.name?.startsWith("block__")
                ) {
                  const bb = getBoundingBox(
                    updatedFormFields?.filter(
                      (f) =>
                        f?.block_id === selectedBlock &&
                        !f?.name?.startsWith("block__"),
                    ),
                    8,
                  );

                  return {
                    ...f,
                    y: bb?.y ?? f?.y,
                    height: bb?.height ?? f?.height,
                  };
                } else {
                  return f;
                }
              }),
        },
      },
    }));

    setOpenEditBlockDialog(false);
    setSelectedBlock(null);
    setDeletedFields([]);
  };

  return (
    <>
      {currentProcessingFile?.document?.tempFields?.formFields
        ?.filter((field) => {
          const rawPage = field?.page_num ?? field?.page;
          const parsedPage = Number(rawPage);
          return Number.isFinite(parsedPage) ? parsedPage === pageNumber : true;
        })
        .map((field, index) => {
          const baseRect = pdfRectToCanvasRect(
            field,
            isPreview ? baseViewPort : viewport || pageHeight,
            scale,
            pageXMin,
            pageYMin,
          );
          const mainRect = { ...baseRect };

          const checkboxRect = { ...baseRect };

          const isPrimarySelected =
            currentProcessingFile?.document?.tempFields?.selectedFormField
              ?.name === field?.name;

          const isExtraSelected =
            currentProcessingFile?.document?.tempFields?.selectedExtraFields?.some(
              (f) =>
                f.name === field.name &&
                (!currentProcessingFile?.document?.tempFields
                  ?.selectedFormField ||
                  currentProcessingFile?.document?.tempFields?.selectedFormField
                    ?.name !== f.name),
            );

          const isUnassigned =
            !field?.block_id ||
            !field?.field_id ||
            (Array.isArray(unassignedFieldNames) &&
              unassignedFieldNames.includes(field?.name));

          return (
            <React.Fragment key={index}>
              {isPrimarySelected && selectToolActive && (
                <Box
                  ref={inputRef}
                  sx={{
                    position: "absolute",
                    left: `${mainRect.left}px`,
                    top: `${mainRect.top}px`,
                    width: `${mainRect.width}px`,
                    height: `${mainRect.height}px`,
                    border: "none",
                    outline: "2px dotted blue",
                    outlineOffset: "0px",
                    boxSizing: "border-box",
                    backgroundColor: "transparent",
                    zIndex: 100,
                  }}
                  onMouseDown={(e) =>
                    handleMouseDown(
                      e,
                      currentProcessingFile,
                      setCurrentProcessingFile,
                      field,
                      scale,
                      "move",
                      "default",
                      pdfContainerRef,
                      setStartPoint,
                      setRect,
                      setIsDrawing,
                      setCurrCursor,
                      setFreeSelectStartPoint,
                      setMoving,
                      setIsSelecting,
                      setInitialMousePosition,
                      setInitialInputSize,
                      setInputPosition,
                      setSelectionRect,
                      setResizing,
                      setResizeDirection,
                      undoRedoStats?.pushHistory,
                    )
                  }
                  onDoubleClick={() => {
                    if (
                      currentProcessingFile?.document?.tempFields
                        ?.selectedExtraFields?.length > 1
                    ) {
                      setCurrentProcessingFile((prevFile) => ({
                        ...prevFile,
                        document: {
                          ...prevFile.document,
                          tempFields: {
                            ...prevFile.document.tempFields,
                            selectedExtraFields:
                              prevFile.document.tempFields.selectedExtraFields.filter(
                                (f) => f.name !== field.name,
                              ),
                          },
                        },
                      }));
                    }
                  }}
                >
                  <SelectionHandles
                    onHandleMouseDown={(e, pos) => {
                      e.stopPropagation();
                      handleMouseDown(
                        e,
                        currentProcessingFile,
                        setCurrentProcessingFile,
                        field,
                        scale,
                        pos.direction,
                        pos.cursor,
                        pdfContainerRef,
                        setStartPoint,
                        setRect,
                        setIsDrawing,
                        setCurrCursor,
                        setFreeSelectStartPoint,
                        setMoving,
                        setIsSelecting,
                        setInitialMousePosition,
                        setInitialInputSize,
                        setInputPosition,
                        setSelectionRect,
                        setResizing,
                        setResizeDirection,
                        undoRedoStats?.pushHistory,
                      );
                    }}
                  />
                </Box>
              )}

              {isExtraSelected && (
                <Box
                  ref={childInputRef}
                  onClick={(e) => {
                    if (currCursor === "crosshair") return;
                    if (e.ctrlKey || e.metaKey) {
                      setCurrentProcessingFile((prevFile) => ({
                        ...prevFile,
                        document: {
                          ...prevFile.document,
                          tempFields: {
                            ...prevFile.document.tempFields,
                            selectedExtraFields:
                              prevFile.document.tempFields.selectedExtraFields.filter(
                                (f) => f.name !== field?.name,
                              ),
                          },
                        },
                      }));
                    } else {
                      setCurrentProcessingFile((prevFile) => ({
                        ...prevFile,
                        document: {
                          ...prevFile.document,
                          tempFields: {
                            ...prevFile.document.tempFields,
                            selectedFormField: field,
                            selectedPdfTextItem: null,
                            selectedExtraPdfTextItems: [],
                          },
                        },
                      }));
                    }
                  }}
                  sx={{
                    position: "absolute",
                    left: `${mainRect.left}px`,
                    top: `${mainRect.top}px`,
                    width: `${mainRect.width}px`,
                    height: `${mainRect.height}px`,
                    border: "none",
                    outline: "2px dotted blue",
                    outlineOffset: "0px",
                    boxSizing: "border-box",
                    backgroundColor: "transparent",
                    zIndex: 20,
                  }}
                />
              )}

              <FieldOverlay
                field={field}
                rect={mainRect}
                checkboxRect={checkboxRect}
                isDrawing={false}
                isSelected={isPrimarySelected}
                selectToolActive={selectToolActive}
                typingToolActive={typingToolActive}
                highlightFields={highlightFields}
                textOverlayDisplayMode={textOverlayDisplayMode}
                focusedField={focusedField}
                currCursor={currCursor}
                isUnassigned={isUnassigned}
                fixedInputs={fixedInputs}
                activeEditorTool={activeEditorTool}
                setCurrentProcessingFile={setCurrentProcessingFile}
                isPreview={isPreview}
                setOpenEditBlockDialog={setOpenEditBlockDialog}
                setSelectedBlock={setSelectedBlock}
                onFocus={(e) => {
                  if (
                    selectToolActive ||
                    fixedInputs.some((prefix) => field.name.startsWith(prefix))
                  ) {
                    e.preventDefault();
                    return;
                  }
                  setFocusedField(field.name);
                }}
                onMouseDown={(e) => {
                  if (
                    (selectToolActive && field.name.startsWith("date_field")) ||
                    fixedInputs.some((prefix) => field.name.startsWith(prefix))
                  ) {
                    e.preventDefault();
                  }
                }}
                onBlur={() => setFocusedField(null)}
                onChange={(e) => {
                  if (
                    fixedInputs.some((prefix) => field.name.startsWith(prefix))
                  ) {
                    return;
                  }

                  if (
                    field.type === "PDFCheckBox" ||
                    field.type === "PDFRadioGroup"
                  ) {
                    if (selectToolActive) return;
                    const isChecked = e.target.checked;
                    setCurrentProcessingFile((prevFile) => ({
                      ...prevFile,
                      document: {
                        ...prevFile.document,
                        tempFields: {
                          ...prevFile.document.tempFields,
                          formFields:
                            prevFile.document.tempFields.formFields.map((f) =>
                              f.name === field.name
                                ? { ...f, value: isChecked }
                                : f,
                            ),
                        },
                      },
                    }));
                  } else {
                    handleFieldChange(e, field);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();

                  if (currCursor === "crosshair") return;

                  if (
                    (e.ctrlKey || e.metaKey) &&
                    currentProcessingFile?.document?.tempFields
                      ?.selectedPdfTextItem
                  ) {
                    setCurrentProcessingFile((prevFile) =>
                      assignLabelFromSelectedPdfText(prevFile, field),
                    );
                    return;
                  }

                  setCurrentProcessingFile((prevFile) => {
                    const isAlreadySelected =
                      prevFile.document.tempFields.selectedExtraFields.some(
                        (f) => f.name === field.name,
                      );

                    return {
                      ...prevFile,
                      document: {
                        ...prevFile.document,
                        tempFields: {
                          ...prevFile.document.tempFields,
                          selectedExtraFields: isAlreadySelected
                            ? prevFile.document.tempFields.selectedExtraFields
                            : e.ctrlKey || e.metaKey
                              ? [
                                  ...prevFile.document.tempFields
                                    .selectedExtraFields,
                                  field,
                                ]
                              : [field],
                          selectedPdfTextItem: null,
                          selectedExtraPdfTextItems: [],
                          selectedFormField: { ...field },
                        },
                      },
                    };
                  });
                }}
                onCheckboxOverlayClick={(e) => {
                  e.stopPropagation();
                  if (currCursor === "crosshair") return;

                  if (
                    (e.ctrlKey || e.metaKey) &&
                    currentProcessingFile?.document?.tempFields
                      ?.selectedPdfTextItem
                  ) {
                    setCurrentProcessingFile((prevFile) =>
                      assignLabelFromSelectedPdfText(prevFile, field),
                    );
                    return;
                  }

                  if (!selectToolActive) {
                    setCurrentProcessingFile((prevFile) => ({
                      ...prevFile,
                      document: {
                        ...prevFile.document,
                        tempFields: {
                          ...prevFile.document.tempFields,
                          formFields:
                            prevFile.document.tempFields.formFields.map((f) =>
                              f.name === field.name
                                ? { ...f, value: !field?.value }
                                : f,
                            ),
                        },
                      },
                    }));
                  }

                  if (
                    currentProcessingFile?.document?.tempFields
                      ?.selectedExtraFields?.length > 0 &&
                    currentProcessingFile?.document?.tempFields?.selectedExtraFields.some(
                      (f) =>
                        f.name === field.name &&
                        (!currentProcessingFile?.document?.tempFields
                          ?.selectedFormField ||
                          currentProcessingFile?.document?.tempFields
                            ?.selectedFormField?.name !== f.name),
                    )
                  ) {
                    if (e.ctrlKey || e.metaKey) {
                      setCurrentProcessingFile((prevFile) => ({
                        ...prevFile,
                        document: {
                          ...prevFile.document,
                          tempFields: {
                            ...prevFile.document.tempFields,
                            selectedExtraFields:
                              prevFile.document.tempFields.selectedExtraFields.filter(
                                (f) => f.name !== field?.name,
                              ),
                          },
                        },
                      }));
                    } else {
                      setCurrentProcessingFile((prevFile) => ({
                        ...prevFile,
                        document: {
                          ...prevFile.document,
                          tempFields: {
                            ...prevFile.document.tempFields,
                            selectedFormField: field,
                            selectedPdfTextItem: null,
                            selectedExtraPdfTextItems: [],
                          },
                        },
                      }));
                    }
                  } else {
                    setCurrentProcessingFile((prevFile) => {
                      const isAlreadySelected =
                        prevFile.document.tempFields.selectedExtraFields.some(
                          (f) => f.name === field.name,
                        );

                      return {
                        ...prevFile,
                        document: {
                          ...prevFile.document,
                          tempFields: {
                            ...prevFile.document.tempFields,
                            selectedExtraFields: isAlreadySelected
                              ? prevFile.document.tempFields.selectedExtraFields
                              : e.ctrlKey || e.metaKey
                                ? [
                                    ...prevFile.document.tempFields
                                      .selectedExtraFields,
                                    field,
                                  ]
                                : [field],
                            selectedPdfTextItem: null,
                            selectedExtraPdfTextItems: [],
                            selectedFormField: { ...field },
                          },
                        },
                      };
                    });
                  }
                }}
              />
            </React.Fragment>
          );
        })}

      <Dialog
        open={openEditBlockDialog}
        onClose={handleCloseEditBlockDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Assignment</DialogTitle>
        <DialogContent>
          <Stack gap={1}>
            {currentProcessingFile?.document?.tempFields?.formFields
              ?.filter(
                (f) =>
                  f?.block_id === selectedBlock &&
                  !f?.name?.startsWith("block__"),
              )
              ?.map((field) => {
                const isFieldDeleted = deletedFields?.includes(field?.name);

                return (
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    key={field?.name}
                  >
                    <Typography>{field?.field_label}</Typography>
                    <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (isFieldDeleted) {
                            setDeletedFields(
                              deletedFields?.filter((f) => f !== field?.name),
                            );
                          } else {
                            setDeletedFields((prev) => [...prev, field?.name]);
                          }
                        }}
                        sx={{
                          border: "1px solid #d0d7de",
                          borderRadius: 1.5,
                          backgroundColor: "transparent",
                          p: "4px !important",
                        }}
                      >
                        {isFieldDeleted ? (
                          <AttachFileIcon
                            sx={{
                              fontSize: "1rem",
                              transform: "rotate(45deg)",
                            }}
                          />
                        ) : (
                          <LinkOffIcon sx={{ fontSize: "1rem" }} />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button
            onClick={() => handleEditConfirm(true)}
            variant="contained"
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            Unassign All
          </Button>
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <Button
              onClick={handleCloseEditBlockDialog}
              variant="text"
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleEditConfirm()}
              variant="contained"
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              Update
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FormFieldOverlayLayer;
